// Revenue attribution: GET /reports/revenue-daily in a DataTable, with a banner
// when refunds_reliable=false (net revenue is currently shown at gross).
//
// Compliance: this is a neutral report of attributed figures — no forward-looking
// promises about revenue or traffic.

import {
  Badge,
  Banner,
  BlockStack,
  Card,
  DataTable,
  EmptyState,
  InlineStack,
  Layout,
  Page,
  Text,
} from "@shopify/polaris";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../api/hooks";
import { useStore } from "../context/StoreProvider";
import { AsyncSection } from "../components/StateViews";
import { formatDate, formatMoney, titleCase } from "../lib/format";

export function RevenuePage() {
  const api = useApi();
  const { store } = useStore();

  // Defaults match the backend's north-star lens: last_non_direct + organic_search.
  const report = useAsync(
    () => api.getRevenueDaily({ store_id: store.id }),
    [api, store.id],
  );

  return (
    <Page
      title="Revenue attribution"
      subtitle="SEO-attributed orders and revenue by day"
    >
      <Layout>
        <AsyncSection
          loading={report.loading}
          error={report.error}
          data={report.data}
          onRetry={report.reload}
          isEmpty={(d) => d.rows.length === 0}
          loadingLabel="Loading revenue report"
          empty={
            <Layout.Section>
              <Card>
                <EmptyState
                  heading="No attributed revenue yet"
                  image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
                >
                  <p>
                    Once orders are attributed to SEO-driven visits, a daily breakdown appears
                    here.
                  </p>
                </EmptyState>
              </Card>
            </Layout.Section>
          }
        >
          {(data) => (
            <>
              {!data.refunds_reliable ? (
                <Layout.Section>
                  <Banner tone="warning" title="Net revenue is shown at gross for now">
                    <Text as="p">
                      The refunds data pipeline isn't connected yet, so refunds read as zero and
                      net revenue equals gross revenue. Treat the net column as an upper bound
                      until refunds are wired in.
                    </Text>
                  </Banner>
                </Layout.Section>
              ) : null}

              <Layout.Section>
                <Card>
                  <BlockStack gap="300">
                    <InlineStack gap="200" blockAlign="center" wrap>
                      <Text as="span" tone="subdued" variant="bodySm">
                        Attribution model
                      </Text>
                      <Badge tone="info">{titleCase(data.model)}</Badge>
                      <Text as="span" tone="subdued" variant="bodySm">
                        Channel
                      </Text>
                      <Badge>{data.channel ? titleCase(data.channel) : "All channels"}</Badge>
                    </InlineStack>

                    <DataTable
                      columnContentTypes={[
                        "text",
                        "text",
                        "numeric",
                        "numeric",
                        "numeric",
                        "numeric",
                        "numeric",
                      ]}
                      headings={[
                        "Date",
                        "Currency",
                        "Orders",
                        "Leads",
                        "Gross",
                        "Refunded",
                        "Net",
                      ]}
                      rows={data.rows.map((r) => [
                        formatDate(r.date),
                        r.currency,
                        r.orders_count,
                        r.leads_count,
                        formatMoney(r.gross_revenue, r.currency),
                        formatMoney(r.refunded_amount, r.currency),
                        formatMoney(r.net_revenue, r.currency),
                      ])}
                    />
                  </BlockStack>
                </Card>
              </Layout.Section>
            </>
          )}
        </AsyncSection>
      </Layout>
    </Page>
  );
}
